import React, { useEffect, useRef, useState } from "react";
import { Field, useFormSubmit, FormFooter } from "./_shared";
import { api } from "../../lib/api";
import { useBranding } from "../Layout";
import { Save } from "lucide-react";

const QUESTIONS = [
  {
    name: "changes_since_purchase",
    label: "Have you made any changes to the home since you purchased it?",
    hint: "Think remodels, finishing the basement, updates, etc.",
    placeholder: "Re-painted the whole interior in 2023, finished the basement (added a 4th bedroom + 3/4 bath), replaced the water heater last summer…",
  },
  {
    name: "favorite_home",
    label: "What do you love most about your home?",
    hint: "Even small things — the morning light, a quiet office nook, the layout. Buyers respond to specifics.",
    placeholder: "The way the kitchen opens to the backyard for summer dinners, the giant primary closet…",
  },
  {
    name: "favorite_neighborhood",
    label: "What do you love most about your neighborhood / community?",
    hint: "Neighbors, schools, parks, walking trails, restaurants, the vibe — anything you'll miss.",
    placeholder: "10 minutes from downtown, neighbors do a 4th of July block party, two parks within walking distance…",
  },
  {
    name: "special_features",
    label: "Any special features we should highlight?",
    hint: "Solar, smart home gear, a workshop, fruit trees, a Tesla charger… little things that make the home stand out.",
    placeholder: "Tesla wall charger in the garage, smart sprinklers, peach + apple trees, mountain view from the back deck…",
  },
];

const DRAFT_ENDPOINT = "/forms/draft/home_story";

export default function HomeStoryForm({ onSubmitted }) {
  const branding = useBranding();
  const [values, setValues] = useState({});
  const [savedAt, setSavedAt] = useState(null);
  const saveTimer = useRef(null);
  const loaded = useRef(false);
  const { submit, busy, err } = useFormSubmit("home_story");

  // Load any existing draft on mount.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(DRAFT_ENDPOINT);
        if (alive) setValues(data.data || {});
      } catch {
        // No draft yet — start clean.
      } finally {
        loaded.current = true;
      }
    })();
    return () => { alive = false; };
  }, []);

  // Debounced auto-save while typing (1s after they stop).
  useEffect(() => {
    if (!loaded.current) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try {
        await api.put(DRAFT_ENDPOINT, { data: values });
        setSavedAt(new Date());
      } catch {
        // Silent: a transient draft-save miss isn't worth interrupting the user.
      }
    }, 1000);
    return () => { if (saveTimer.current) clearTimeout(saveTimer.current); };
  }, [values]);

  const upd = (k) => (e) => setValues((prev) => ({ ...prev, [k]: e.target.value }));

  return (
    <form
      onSubmit={(e) => { e.preventDefault(); submit(values, onSubmitted); }}
      className="card"
      style={{ padding: 32, display: "flex", flexDirection: "column", gap: 24 }}
      data-testid="home-story-form"
    >
      <div style={{ color: "var(--text-muted)", fontSize: 14, lineHeight: 1.55, padding: "12px 16px", background: "#F0EDE3", borderRadius: 12, border: "1px solid var(--border)" }}>
        <b style={{ display: "block", marginBottom: 4, color: "var(--text)" }}>Your story sells the home.</b>
        {branding.agent_first_name} uses your answers to write the listing description, social posts, and brochure — so the
        more specific you can be, the more your home stands out. Don't overthink it; bullets, fragments,
        or full sentences all work. You can save now and finish later.
      </div>

      {QUESTIONS.map((q) => (
        <Field key={q.name} label={q.label} hint={q.hint}>
          <textarea
            data-testid={`home-story-${q.name}`}
            className="input-field"
            rows={5}
            value={values[q.name] || ""}
            onChange={upd(q.name)}
            placeholder={q.placeholder}
          />
        </Field>
      ))}

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
        <span style={{ fontSize: 12, color: "var(--text-muted)", display: "inline-flex", alignItems: "center", gap: 6 }}>
          <Save size={12} /> {savedAt ? `Draft saved ${savedAt.toLocaleTimeString()}` : "Draft saves automatically as you type"}
        </span>
      </div>

      <FormFooter busy={busy} err={err} testid="home-story" />
    </form>
  );
}
